/**
 * NativeScript / Electron platform helpers.
 *
 * IMPORTANT: Use explicit `globalThis.X` member-access reads (rather
 * than bare-identifier `typeof X`) when probing for runtime-added
 * globals like `NSObject`, `NSString`, `android`, `java`, `window`.
 *
 * Background: under `@nativescript/vite` HMR, modules are served as
 * real ES modules over HTTP and instantiated by the iOS V8 ESM
 * loader. Bare-identifier resolution at the module level walks
 * ModuleEnvironmentRecord → outer GlobalEnvironmentRecord →
 * ObjectEnvironmentRecord wrapping the global object — and in the
 * iOS V8 build NS ships, that final hop to the global is **not
 * consistent across modules**: some modules see runtime-added own
 * properties on the global via bare identifier, some see them as
 * `undefined` even when `globalThis.X` is populated.
 *
 * `globalThis` is a real builtin global and explicit property access
 * on it (`globalThis.NSObject`) bypasses the buggy
 * ObjectEnvironmentRecord hop entirely. V8 always evaluates this
 * consistently.
 * 
 * In webpack / non-HMR Vite this is harmless: `globalThis.NSObject`
 * resolves the same way it always has.
 */

const upsGlobal = globalThis as Record<string, unknown>;

/**
 * Determine if running on native iOS mobile app
 */
export function isIOS(): boolean {
  return (
    typeof upsGlobal.NSObject !== 'undefined' &&
    typeof upsGlobal.NSString !== 'undefined'
  );
}

/**
 * Determine if running on native Android mobile app
 */
export function isAndroid(): boolean {
  return (
    typeof upsGlobal.android !== 'undefined' &&
    typeof upsGlobal.java !== 'undefined'
  );
}

/**
 * Determine if running on native iOS or Android mobile app
 */
export function isNativeScript(): boolean {
  return isIOS() || isAndroid();
}

/**
 * Electron helpers
 */
export function isElectron(): boolean {
  const win = upsGlobal.window as { process?: { type?: unknown } } | undefined;
  return typeof win !== 'undefined' && !!win.process && !!win.process.type;
}
